/* カートページのクーポンコード入力欄
 * 表示側：sections/main-cart-footer.liquid（<cart-coupon>）
 * 見た目：assets/custom-cart-coupon.css
 *
 * 動き：
 *   1. 入力されたコードを、すでにカートに入っているコード（GET /cart.js の
 *      discount_codes）と合わせて /cart/update.js の discount に送る。
 *      ※ Liquid 側の cart.discount_codes は使えないストアがあるため、
 *        適用中のコードは常に /cart.js から取り、タグも JS で描画する。
 *   2. レスポンスの discount_codes[].applicable が false（存在しない／条件未達）
 *      なら、そのコードを外してエラー表示。
 *      すでに入っていたコードが外れた場合（併用不可のクーポン同士）は
 *      その旨を表示する。
 *   3. 成功時はレスポンスの sections でカート本体と小計を描き直す。
 *      描き直しで <cart-coupon> 自体も作り直されるので、イベントは
 *      constructor で毎回張り直す（テーマの cart-items と同じ考え方）。
 *   4. update.js が discount_codes を返さないストア（未対応）の場合だけ、
 *      /discount/コード?redirect=/cart にリダイレクトして適用する。
 *
 * 注意：複数クーポンの同時適用は、管理画面のディスカウント設定で
 *       「組み合わせ」が許可されている場合のみ有効。許可されていない
 *       クーポン同士は Shopify 側で片方しか残らない。
 */
class CartCoupon extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.input = this.querySelector('.cart-coupon__input');
    this.message = this.querySelector('.cart-coupon__error');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.bindRemoveButtons();
  }

  connectedCallback() {
    // 初期表示：カートに入っているコードをタグで出す
    this.refreshTags();
  }

  bindRemoveButtons() {
    this.querySelectorAll('.cart-coupon__remove').forEach((btn) => {
      btn.addEventListener('click', this.onRemove.bind(this));
    });
  }

  sameCode(a, b) {
    return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
  }

  cartUrl() {
    const base = (window.routes && window.routes.cart_url) || '/cart';
    return `${base}.js`;
  }

  async currentCodes() {
    try {
      const response = await fetch(this.cartUrl(), { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const cart = await response.json();
      return Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    } catch (e) {
      return [];
    }
  }

  async refreshTags() {
    const codes = await this.currentCodes();
    if (this.isConnected) this.renderTags(codes);
  }

  async onSubmit(event) {
    event.preventDefault();
    const code = this.input.value.trim();
    if (!code) {
      this.showMessage('クーポンコードを入力してください。');
      this.input.focus();
      return;
    }
    this.setBusy(true);
    const existing = (await this.currentCodes()).map((d) => d.code).filter((c) => !this.sameCode(c, code));
    this.apply([...existing, code], code, existing);
  }

  async onRemove(event) {
    const code = event.currentTarget.dataset.code;
    this.setBusy(true);
    const existing = (await this.currentCodes()).map((d) => d.code);
    this.apply(existing.filter((c) => !this.sameCode(c, code)), null, []);
  }

  sectionsToRender() {
    return [
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.js-contents' },
    ]
      .map((s) => {
        const el = document.getElementById(s.id);
        return el ? { ...s, section: el.dataset.id } : null;
      })
      .filter(Boolean);
  }

  async apply(codes, newCode, previousCodes) {
    this.setBusy(true);
    this.showMessage('');
    const sections = this.sectionsToRender();
    try {
      const cart = await this.updateCart({
        discount: codes.join(','),
        sections: sections.map((s) => s.section).join(','),
        sections_url: window.location.pathname,
      });

      // update.js が discount_codes を返さない = このストアではカートAPIの
      // クーポン適用が使えない。URL方式に切り替える。
      if (!Array.isArray(cart.discount_codes)) {
        if (newCode) {
          window.location.href = `/discount/${encodeURIComponent(newCode)}?redirect=${encodeURIComponent(
            window.location.pathname
          )}`;
          return;
        }
        window.location.reload();
        return;
      }

      let notice = '';
      if (newCode) {
        const entry = cart.discount_codes.find((d) => this.sameCode(d.code, newCode));
        if (!entry || !entry.applicable) {
          // 無効なコードをカートに残さない（次回以降のエラー表示を避ける）
          const rest = cart.discount_codes.filter((d) => !this.sameCode(d.code, newCode)).map((d) => d.code);
          await this.updateCart({ discount: rest.join(',') });
          // すでに別のコードが入っている場合、Shopify 側の「組み合わせ」設定で
          // 併用が許可されていないと、正しいコードでも applicable:false になる
          this.showMessage(
            previousCodes.length
              ? 'このクーポンコードは使用できないか、適用中のクーポンと併用できません。'
              : 'このクーポンコードは使用できません。コードをご確認ください。'
          );
          this.input.select();
          return;
        }
        // 併用不可などで、それまで有効だったコードが外れた場合は知らせる
        const dropped = previousCodes.filter((prev) => {
          const now = cart.discount_codes.find((d) => this.sameCode(d.code, prev));
          return !now || !now.applicable;
        });
        if (dropped.length) {
          notice = `「${dropped.join('」「')}」は「${newCode}」と併用できないため解除されました。`;
          // 外れたコードはカートからも取り除き、タグ表示とメッセージを一致させる
          const remaining = cart.discount_codes.filter((d) => d.applicable).map((d) => d.code);
          const refreshed = await this.updateCart({
            discount: remaining.join(','),
            sections: sections.map((s) => s.section).join(','),
            sections_url: window.location.pathname,
          });
          if (Array.isArray(refreshed.discount_codes)) Object.assign(cart, refreshed);
        }
      }

      this.renderSections(sections, cart.sections || {});
      const next = document.querySelector('cart-coupon') || this;
      next.renderTags(cart.discount_codes);
      if (notice) next.showMessage(notice);
      if (next.input) next.input.focus();
    } catch (e) {
      this.showMessage('通信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      this.setBusy(false);
    }
  }

  async updateCart(body) {
    const response = await fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`cart update failed: ${response.status}`);
    return response.json();
  }

  renderSections(sections, html) {
    sections.forEach((s) => {
      if (!html[s.section]) return;
      const target = document.getElementById(s.id);
      if (!target) return;
      const fresh = new DOMParser().parseFromString(html[s.section], 'text/html').querySelector(s.selector);
      const current = target.querySelector(s.selector);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    });
  }

  renderTags(codes) {
    let wrap = this.querySelector('.cart-coupon__applied-wrap');
    if (!codes.length) {
      if (wrap) wrap.remove();
      return;
    }
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'cart-coupon__applied-wrap';
      wrap.innerHTML =
        '<p class="cart-coupon__applied-label">適用中のクーポン</p>' +
        '<ul class="cart-coupon__applied list-unstyled" role="list" aria-label="適用中のクーポン"></ul>';
      this.appendChild(wrap);
    }
    const list = wrap.querySelector('.cart-coupon__applied');
    const icon =
      '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-discount" viewBox="0 0 12 12">' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path></svg>';
    list.innerHTML = codes
      .map((d) => {
        const code = this.escape(d.code);
        const inactive = d.applicable === false;
        return (
          `<li class="cart-coupon__tag${inactive ? ' cart-coupon__tag--inactive' : ''}">` +
          icon +
          `<span>${code}</span>` +
          (inactive ? '<small>（適用条件を満たしていません）</small>' : '') +
          `<button type="button" class="cart-coupon__remove" data-code="${code}" aria-label="クーポン ${code} を削除">&times;</button>` +
          '</li>'
        );
      })
      .join('');
    this.bindRemoveButtons();
  }

  escape(text) {
    return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  showMessage(text) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = !text;
  }

  setBusy(state) {
    this.setAttribute('aria-busy', state ? 'true' : 'false');
  }
}

if (!customElements.get('cart-coupon')) {
  customElements.define('cart-coupon', CartCoupon);
}
